import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditPlanMutation } from "@modules/plans/services/mutations";
import { PlanFormValues, planSchema } from "@modules/plans/validations";
import { listPlansQueryKey } from "@modules/plans/services/queries/listPlans/query";

export function useEditPlanModal(
  modalRef: RefObject<ModalRef>,
  onComplete?: () => void,
  agreement?: string
) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<PlanFormValues>({
    resolver: zodResolver(planSchema()),
    mode: "onBlur",
    defaultValues: {
      agreement: agreement ? Number(agreement) : undefined,
    },
  });

  const { mutateAsync, isPending } = useEditPlanMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listPlansQueryKey,
        refetchType: "all",
      });

      open?.({
        type: "success",
        message: translate("subscription_plan_admin.success.edit"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: PlanFormValues) => {
    const planId = modalRef.current?.getId();
    await mutateAsync({ id: planId as string, data });
  };

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
